import React, { useEffect, useMemo, useState } from "react";
import api, { dateDK, formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Wrench, AlertTriangle, ChevronRight, Plus, Check, Search } from "lucide-react";
import { toast } from "sonner";

const CHECK_ITEMS = [
  { key: "kabine_ren", label: "Kabinen ren?", group: "Renhed" },
  { key: "udvendigt_ren", label: "Udvendigt ren?", group: "Renhed" },
  { key: "olie", label: "Olie OK?", group: "Væsker" },
  { key: "koelervaeske", label: "Kølervæske OK?", group: "Væsker" },
  { key: "sprinklervaeske", label: "Sprinklervæske OK?", group: "Væsker" },
  { key: "bremser", label: "Bremser OK?", group: "Køretur" },
  { key: "daek", label: "Dæk OK?", group: "Køretur" },
  { key: "skader_ok", label: "Ingen nye skader?", group: "Skader" },
];

export default function Kontrol() {
  const [data, setData] = useState(null);
  const [openCar, setOpenCar] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [statusFilter, setStatusFilter] = useState("active"); // active | lager | all
  const [sortBy, setSortBy] = useState("kontrol"); // kontrol | service | plade
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/inspections/status");
      setData(r.data);
      const t = await api.get("/tasks", { params: { status_filter: "open" } });
      setTasks(t.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const tasksByCar = useMemo(() => {
    const m = {};
    tasks.forEach((t) => {
      if (!m[t.car_id]) m[t.car_id] = [];
      m[t.car_id].push(t);
    });
    return m;
  }, [tasks]);

  // Service-data pr. bil — fetched fra /cars
  const [serviceMap, setServiceMap] = useState({});
  const [forecasts, setForecasts] = useState({});
  useEffect(() => {
    api.get("/cars").then(async (r) => {
      const map = {};
      r.data.forEach((c) => { map[c.id] = c; });
      setServiceMap(map);
      const fc = {};
      await Promise.all(
        r.data
          .filter((c) => c.status === "active" && c.current_km != null && c.last_service_km != null)
          .map(async (c) => {
            try {
              const fr = await api.get(`/cars/${c.id}/service-forecast`);
              fc[c.id] = fr.data;
            } catch { /* ignore */ }
          })
      );
      setForecasts(fc);
    }).catch(() => {});
  }, [data]);

  return (
    <div className="p-4 sm:p-10 max-w-5xl" data-testid="kontrol-page">
      <div className="overline text-zinc-500">Klargøring & service</div>
      <h1 className="headline text-3xl sm:text-5xl mt-2 mb-2">Kontrol.</h1>
      <p className="text-zinc-400 text-sm mb-6">
        Krav: alle aktive biler skal kontrolleres hver 30. dag. Service hver 15.000 km. Tap en bil for at se detaljer.
      </p>

      {/* Filter-toggle */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Tab value="active" cur={statusFilter} onClick={setStatusFilter}>Aktive biler</Tab>
        <Tab value="lager" cur={statusFilter} onClick={setStatusFilter}>Lagerbiler</Tab>
        <Tab value="all" cur={statusFilter} onClick={setStatusFilter}>Begge</Tab>
        <div className="relative flex-1 min-w-[200px] max-w-md ml-auto">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søg plade, mærke, model…"
            className="pl-9 rounded-none border-zinc-700 h-9"
            data-testid="kontrol-search"
          />
        </div>
      </div>
      {/* Sortering — kun relevant for aktive */}
      {statusFilter !== "lager" && (
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Sortér efter</span>
          <Tab value="kontrol" cur={sortBy} onClick={setSortBy} small>Kontrol — mest aktuelle</Tab>
          <Tab value="service" cur={sortBy} onClick={setSortBy} small>Service — mest aktuelle</Tab>
          <Tab value="plade" cur={sortBy} onClick={setSortBy} small>Nummerplade</Tab>
        </div>
      )}

      {data?.rows.length === 0 && (
        <div className="text-zinc-500">Ingen biler.</div>
      )}

      {(() => {
        let rows = data?.rows || [];
        if (statusFilter === "active") rows = rows.filter((r) => r.status !== "lager");
        else if (statusFilter === "lager") rows = rows.filter((r) => r.status === "lager");
        // search-filter
        const s = search.toLowerCase().trim();
        if (s) {
          rows = rows.filter((r) => {
            const hay = [r.license_plate, r.make, r.model].filter(Boolean).join(" ").toLowerCase();
            return hay.includes(s);
          });
        }
        // sort
        const sorted = rows.slice().sort((a, b) => {
          if (sortBy === "service" && statusFilter !== "lager") {
            const ka = serviceKmLeft(serviceMap[a.car_id]);
            const kb = serviceKmLeft(serviceMap[b.car_id]);
            // mangler km-data → øverst (priority)
            if (ka == null && kb == null) return (a.license_plate || "").localeCompare(b.license_plate || "");
            if (ka == null) return -1;
            if (kb == null) return 1;
            return ka - kb;
          }
          if (sortBy === "kontrol" && statusFilter !== "lager") {
            // Aldrig kontrolleret → øverst, ellers ældste først
            const da = a.last_inspection_date ? a.days_since_inspection : 99999;
            const db = b.last_inspection_date ? b.days_since_inspection : 99999;
            return db - da; // flest dage siden = øverst
          }
          return (a.license_plate || "").localeCompare(b.license_plate || "");
        });
        if (sorted.length === 0) {
          return <div className="text-zinc-500 text-sm py-6">Ingen biler i denne visning.</div>;
        }
        return <CarList rows={sorted} serviceMap={serviceMap} forecasts={forecasts} onOpen={setOpenCar} />;
      })()}

      {openCar && (
        <InspectionDialog
          car={openCar}
          openTasks={tasksByCar[openCar.car_id] || []}
          serviceCar={serviceMap[openCar.car_id]}
          onClose={() => setOpenCar(null)}
          onSaved={() => { setOpenCar(null); load(); }}
        />
      )}
    </div>
  );
}

function Tab({ value, cur, onClick, children, small }) {
  const active = cur === value;
  const sizeCls = small ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs";
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={
        sizeCls + " uppercase tracking-wider transition-colors " +
        (active ? "bg-[#C8A661] text-black font-semibold" : "bg-zinc-800 text-zinc-400 hover:text-white")
      }
      data-testid={`kontrol-${small ? "sort" : "filter"}-${value}`}
    >
      {children}
    </button>
  );
}

function serviceKmLeft(sc) {
  if (!sc || sc.current_km == null || sc.last_service_km == null) return null;
  const interval = Number(sc.service_interval_km) || 15000;
  return interval - (Number(sc.current_km) - Number(sc.last_service_km));
}

function CarList({ rows, serviceMap, forecasts, onOpen }) {
  return (
    <div className="space-y-3">
      {rows.map((r) => {
        const overdue = r.overdue;
        const days = r.days_since_inspection;
        const isLager = r.status === "lager";
        const sc = serviceMap[r.car_id];
        const fc = forecasts[r.car_id];
        const hasServiceData = sc && sc.current_km != null && sc.last_service_km != null;
        const driven = hasServiceData ? Number(sc.current_km) - Number(sc.last_service_km) : null;
        const carInterval = (sc && Number(sc.service_interval_km)) || 15000;
        const kmToNext = hasServiceData ? carInterval - driven : null;
        const serviceDue = kmToNext != null && kmToNext <= 0;
        const serviceClose = kmToNext != null && kmToNext > 0 && kmToNext <= 1000;
        const serviceWarn = kmToNext != null && kmToNext > 1000 && kmToNext <= 3000;
        return (
          <button
            key={r.car_id}
            onClick={() => onOpen(r)}
            className={
              "w-full text-left section-card p-4 sm:p-5 transition-colors hover:bg-[#262626] " +
              (overdue || serviceDue ? "row-warning" : "")
            }
            data-testid={`kontrol-car-${r.license_plate}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold text-xl tracking-tight text-white">{r.license_plate}</span>
                  {r.make && <span className="text-zinc-400 text-sm">{r.make} {r.model}</span>}
                  {isLager && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-zinc-400 border border-zinc-700 px-1.5 py-0.5">
                      Lager
                    </span>
                  )}
                  {r.needs_workshop && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-400 border border-red-700 px-2 py-0.5">
                      <Wrench className="w-3 h-3" /> SKAL PÅ VÆRKSTED
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-zinc-500 shrink-0 mt-1" />
            </div>

            {/* Kontrol + service som store statistik-bokse */}
            {!isLager && (
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-3">
                {/* Kontrol-boks */}
                <div className={
                  "p-2.5 sm:p-3 border " +
                  (!r.last_inspection_date ? "border-red-700 bg-red-950/20" :
                    overdue ? "border-red-700 bg-red-950/20" :
                    days > 20 ? "border-amber-700 bg-amber-950/20" :
                    "border-zinc-800 bg-zinc-900/40")
                }>
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500">Kontrol</div>
                  {!r.last_inspection_date ? (
                    <>
                      <div className="font-display font-black text-xl sm:text-2xl tracking-tighter text-red-400 mt-0.5">
                        Aldrig
                      </div>
                      <div className="text-[10px] text-red-300 mt-0.5 inline-flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> kontrolleres straks
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={"font-display font-black text-xl sm:text-2xl tracking-tighter mt-0.5 " + (
                        overdue ? "text-red-400" : days > 20 ? "text-amber-400" : "text-white"
                      )}>
                        {days} dage
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">
                        siden {dateDK(r.last_inspection_date)}
                        {overdue && <span className="text-red-400 font-semibold"> · overskredet</span>}
                      </div>
                    </>
                  )}
                </div>

                {/* Service-boks */}
                <div className={
                  "p-2.5 sm:p-3 border " +
                  (!hasServiceData ? "border-amber-700 bg-amber-950/20" :
                    serviceDue ? "border-red-700 bg-red-950/20" :
                    serviceClose ? "border-red-700 bg-red-950/20" :
                    serviceWarn ? "border-amber-700 bg-amber-950/20" :
                    "border-zinc-800 bg-zinc-900/40")
                }>
                  <div className="text-[9px] uppercase tracking-wider text-zinc-500">Service</div>
                  {!hasServiceData ? (
                    <>
                      <div className="font-display font-black text-xl sm:text-2xl tracking-tighter text-amber-400 mt-0.5">
                        Mangler
                      </div>
                      <div className="text-[10px] text-amber-300 mt-0.5">
                        udfyld km-data
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={"font-display font-black text-xl sm:text-2xl tracking-tighter mt-0.5 " + (
                        serviceDue || serviceClose ? "text-red-400" :
                        serviceWarn ? "text-amber-400" :
                        "text-white"
                      )}>
                        {serviceDue
                          ? `+${new Intl.NumberFormat("da-DK").format(Math.abs(kmToNext))} km`
                          : `${new Intl.NumberFormat("da-DK").format(kmToNext)} km`}
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">
                        {serviceDue
                          ? <span className="text-red-400 font-semibold">forfalden</span>
                          : <>til næste{fc?.expected_service_date ? ` · ${dateDK(fc.expected_service_date)}` : ""}</>}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {isLager && (
              <div className="text-xs mt-2 text-zinc-500">
                {r.last_inspection_date
                  ? <>Sidste kontrol: {dateDK(r.last_inspection_date)}</>
                  : "På lager — ingen kontrol-krav"}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

function InspectionDialog({ car, openTasks, serviceCar, onClose, onSaved }) {
  const [checks, setChecks] = useState(() =>
    Object.fromEntries(CHECK_ITEMS.map((i) => [i.key, false]))
  );
  const [skaderNote, setSkaderNote] = useState("");
  const [needsWorkshop, setNeedsWorkshop] = useState(car.needs_workshop || false);
  const [workshopReason, setWorkshopReason] = useState(car.workshop_reason || "");
  const [workshopSaving, setWorkshopSaving] = useState(false);
  const [workshopDirty, setWorkshopDirty] = useState(false);
  const [taskDraft, setTaskDraft] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [tasks, setTasks] = useState(openTasks);
  const [tasksDirty, setTasksDirty] = useState(false);
  const [addingTask, setAddingTask] = useState(false);

  // Service-state (inline opdatering)
  const [serviceForm, setServiceForm] = useState({
    last_service_date: serviceCar?.last_service_date || "",
    last_service_km: serviceCar?.last_service_km ?? "",
    current_km: serviceCar?.current_km ?? "",
    service_notes: serviceCar?.service_notes || "",
  });
  const [serviceDirty, setServiceDirty] = useState(false);
  const [serviceSaving, setServiceSaving] = useState(false);

  const saveService = async () => {
    setServiceSaving(true);
    try {
      const body = {
        last_service_date: serviceForm.last_service_date || null,
        last_service_km: serviceForm.last_service_km === "" ? null : Number(serviceForm.last_service_km),
        current_km: serviceForm.current_km === "" ? null : Number(serviceForm.current_km),
        service_notes: serviceForm.service_notes || "",
      };
      await api.patch(`/cars/${car.car_id}`, body);
      toast.success("Service opdateret");
      setServiceDirty(true);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setServiceSaving(false);
    }
  };

  // Gem værksted-status med det samme — så det også virker for lagerbiler
  // og man kan slå det fra uden at lave en hel kontrol.
  const persistWorkshop = async (value, reason) => {
    setWorkshopSaving(true);
    try {
      await api.patch(`/cars/${car.car_id}`, {
        needs_workshop: value,
        workshop_reason: value ? (reason || "") : "",
      });
      setWorkshopDirty(true);
      toast.success(value ? "Markeret: skal på værksted" : "Værksted-status fjernet");
    } catch (e) {
      toast.error(formatErr(e));
      setNeedsWorkshop(!value); // rollback
    } finally {
      setWorkshopSaving(false);
    }
  };

  const onToggleWorkshop = (checked) => {
    setNeedsWorkshop(checked);
    persistWorkshop(checked, workshopReason);
  };

  const onBlurReason = () => {
    if (needsWorkshop && workshopReason !== (car.workshop_reason || "")) {
      persistWorkshop(true, workshopReason);
    }
  };

  const addTask = async () => {
    const d = taskDraft.trim();
    if (!d) return;
    setAddingTask(true);
    try {
      const r = await api.post("/tasks", { car_id: car.car_id, description: d });
      setTasks((arr) => [...arr, r.data]);
      setTaskDraft("");
      setTasksDirty(true);
      toast.success("Opgave tilføjet");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setAddingTask(false);
    }
  };

  const completeTask = async (t) => {
    try {
      await api.patch(`/tasks/${t.id}`, { status: "done" });
      setTasks((arr) => arr.filter((x) => x.id !== t.id));
      setTasksDirty(true);
      toast.success("Opgave markeret udført");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const closeDialog = () => {
    if (tasksDirty || workshopDirty || serviceDirty) onSaved();
    else onClose();
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/inspections", {
        car_id: car.car_id,
        ...checks,
        skader_note: skaderNote,
        notes,
        needs_workshop: needsWorkshop,
        workshop_reason: workshopReason,
        new_tasks: [],
      });
      toast.success("Kontrol gemt");
      onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };
  const groups = ["Renhed", "Væsker", "Køretur", "Skader"];

  const isLager = car.status === "lager";

  return (
    <Dialog open={true} onOpenChange={(v) => !v && closeDialog()}>
      <DialogContent className="rounded-none max-w-lg max-h-[95vh] overflow-y-auto p-4 sm:p-6" data-testid="inspection-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tighter text-2xl sm:text-3xl">
            {isLager ? "Lagerbil" : "Kontrol"} · {car.license_plate}
          </DialogTitle>
          {isLager && (
            <p className="text-xs text-zinc-500 mt-1">På lager — ingen kontrol-krav. Du kan oprette og afslutte opgaver.</p>
          )}
        </DialogHeader>

        <div className="space-y-5">
          {!isLager && groups.map((g) => (
            <div key={g}>
              <div className="cost-group-label">{g}</div>
              <div className="space-y-1">
                {CHECK_ITEMS.filter((c) => c.group === g).map((c) => (
                  <label
                    key={c.key}
                    className="flex items-center gap-3 py-3 px-3 cursor-pointer hover:bg-[#262626] active:bg-[#2A2A2A] border border-zinc-800 transition-colors"
                    data-testid={`check-${c.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={checks[c.key]}
                      onChange={(e) => setChecks({ ...checks, [c.key]: e.target.checked })}
                      className="w-6 h-6 cursor-pointer accent-[#C8A661]"
                    />
                    <span className="flex-1 text-base">{c.label}</span>
                    {checks[c.key] && <Check className="w-5 h-5 text-emerald-400" />}
                  </label>
                ))}
              </div>
              {g === "Skader" && (
                <Textarea
                  placeholder="Beskriv evt. nye skader…"
                  value={skaderNote}
                  onChange={(e) => setSkaderNote(e.target.value)}
                  className="rounded-none border-zinc-700 mt-2 min-h-[60px]"
                  data-testid="skader-note"
                />
              )}
            </div>
          ))}

          {/* Opgaver — gemmes med det samme */}
          <div>
            <div className="cost-group-label">Opgaver</div>
            <div className="flex gap-2">
              <Input
                placeholder="Fx Dæk skal skiftes"
                value={taskDraft}
                onChange={(e) => setTaskDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTask(); } }}
                className="rounded-none border-zinc-700 flex-1"
                data-testid="new-task-input"
                disabled={addingTask}
              />
              <Button
                type="button"
                onClick={addTask}
                disabled={addingTask || !taskDraft.trim()}
                className="rounded-none bg-[#C8A661] text-black hover:bg-[#D4B574]"
                data-testid="add-task-button"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-zinc-500 mt-2">
              Opgaver gemmes med det samme — kontrol behøver ikke at være udført.
            </p>
            {tasks.length > 0 && (
              <div className="space-y-1 mt-3">
                {tasks.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 py-2.5 px-3 border border-zinc-800 bg-[#1F1F1F]" data-testid={`open-task-${t.id}`}>
                    <button
                      onClick={() => completeTask(t)}
                      className="w-6 h-6 border-2 border-zinc-600 hover:border-emerald-500 transition-colors flex items-center justify-center shrink-0"
                      title="Marker udført"
                      data-testid={`complete-task-${t.id}`}
                    >
                    </button>
                    <span className="flex-1 text-sm">{t.description}</span>
                    <span className="text-xs text-zinc-500">{dateDK(t.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Værksted — gemmes med det samme */}
          <div>
            <div className="cost-group-label">Værksted</div>
            <label className="flex items-center gap-3 py-3 px-3 cursor-pointer border border-zinc-800 hover:bg-[#262626]">
              <input
                type="checkbox"
                checked={needsWorkshop}
                onChange={(e) => onToggleWorkshop(e.target.checked)}
                disabled={workshopSaving}
                className="w-6 h-6 cursor-pointer accent-red-500"
                data-testid="needs-workshop-toggle"
              />
              <span className="text-base flex-1">Bilen skal på værksted</span>
              {workshopSaving && <span className="text-xs text-zinc-500">Gemmer…</span>}
              {!workshopSaving && needsWorkshop && <Wrench className="w-4 h-4 text-red-400" />}
            </label>
            {needsWorkshop && (
              <Input
                placeholder="Årsag (fx 'larmer fra hjulleje')"
                value={workshopReason}
                onChange={(e) => setWorkshopReason(e.target.value)}
                onBlur={onBlurReason}
                className="rounded-none border-zinc-700 mt-2"
                data-testid="workshop-reason"
              />
            )}
            <p className="text-[11px] text-zinc-500 mt-2">
              Gemmes med det samme — virker også for lagerbiler.
            </p>
          </div>

          {/* Service — kun for aktive biler */}
          {!isLager && (
            <div>
              <div className="cost-group-label flex items-center gap-2">
                <Wrench className="w-3.5 h-3.5" /> Service
                {(() => {
                  const lk = Number(serviceForm.last_service_km);
                  const ck = Number(serviceForm.current_km);
                  if (!lk || !ck) return null;
                  const drove = ck - lk;
                  const intv = Number(serviceCar?.service_interval_km) || 15000;
                  const left = intv - drove;
                  return (
                    <span className={"text-[11px] tracking-normal font-sans " + (
                      left <= 0 ? "text-red-400 font-semibold" :
                      left <= 1000 ? "text-red-400 font-semibold" :
                      left <= 3000 ? "text-amber-400" : "text-zinc-500"
                    )}>
                      {left <= 0
                        ? `Forfalden (${new Intl.NumberFormat("da-DK").format(Math.abs(left))} km over)`
                        : `${new Intl.NumberFormat("da-DK").format(left)} km til næste`}
                    </span>
                  );
                })()}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Sidste service-dato</Label>
                  <Input
                    type="date"
                    value={serviceForm.last_service_date}
                    onChange={(e) => setServiceForm({ ...serviceForm, last_service_date: e.target.value })}
                    className="rounded-none border-zinc-700 mt-1 h-9"
                    data-testid="kontrol-service-date"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Km ved sidste service</Label>
                  <Input
                    type="number"
                    value={serviceForm.last_service_km}
                    onChange={(e) => setServiceForm({ ...serviceForm, last_service_km: e.target.value })}
                    className="rounded-none border-zinc-700 mt-1 h-9"
                    data-testid="kontrol-service-km"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Nuværende km</Label>
                  <Input
                    type="number"
                    value={serviceForm.current_km}
                    onChange={(e) => setServiceForm({ ...serviceForm, current_km: e.target.value })}
                    className="rounded-none border-zinc-700 mt-1 h-9"
                    data-testid="kontrol-current-km"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Service-notat (valgfri)</Label>
                  <Textarea
                    value={serviceForm.service_notes}
                    onChange={(e) => setServiceForm({ ...serviceForm, service_notes: e.target.value })}
                    placeholder="Fx Olieskift, bremseklodser foran"
                    className="rounded-none border-zinc-700 mt-1 min-h-[50px] text-xs"
                    data-testid="kontrol-service-notes"
                  />
                </div>
              </div>
              <Button
                type="button"
                onClick={saveService}
                disabled={serviceSaving}
                className="rounded-none bg-zinc-800 hover:bg-zinc-700 text-white h-8 text-xs mt-2"
                data-testid="kontrol-save-service"
              >
                {serviceSaving ? "Gemmer…" : "Opdater service"}
              </Button>
            </div>
          )}

          {/* Kommentar */}
          <div>
            <Label className="overline">Kommentar (valgfri)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="rounded-none border-zinc-700 mt-2 min-h-[60px]"
              placeholder="Generelle bemærkninger…"
              data-testid="kontrol-notes"
            />
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 mt-2">
          <Button
            type="button"
            variant="outline"
            className="rounded-none border-zinc-700 w-full sm:w-auto"
            onClick={closeDialog}
            data-testid="kontrol-cancel"
          >
            Luk
          </Button>
          {!isLager && (
            <Button
              type="button"
              onClick={submit}
              disabled={saving}
              className="rounded-none bg-[#C8A661] text-black hover:bg-[#D4B574] w-full sm:w-auto h-12 text-base font-bold"
              data-testid="kontrol-save"
            >
              {saving ? "Gemmer…" : "Gem kontrol"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
